import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { change } from 'redux-form'
import { versantDiagnosisCodesLoad, versantProcedureCodesLoad } from '../../../../../../../../../redux/actions/addOns/rpa/vision_insurance/versant/versantAction'
import { formatVersantCodeLabel, mapVersantCodeToOption, normalizeVersantCodes } from '../versantClaimHelper'

const MIN_SEARCH_LENGTH = 3
let searchTimer

const isProcedureField = (name) => name?.includes('procedureCodes')

const toCodePayload = (data) => ({
  codeID: data.codeID,
  code: data.code,
  codeType: data.codeType,
  description: data.description,
  isDiabetic: data.isDiabetic,
})

function VersantSearchSelect(props) {
  const {
    input,
    meta: { touched, error },
    label,
    wrapperClasses,
    claimId,
    dateOfService,
    serviceCategories,
    sourceSubSystem,
    codes,
    loading,
    dispatch,
    meta,
  } = props

  const rootRef = useRef(null)
  const searchInputRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const selectedCodes = normalizeVersantCodes(input.value)
  const isProcedure = isProcedureField(input.name)
  const results = searchText.length >= MIN_SEARCH_LENGTH
    ? (codes || []).map(mapVersantCodeToOption)
    : []

  useEffect(() => {
    if (!open) return undefined

    const handleOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false)
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [open])

  const handleSearch = (value) => {
    setSearchText(value)
    if (value.length < MIN_SEARCH_LENGTH || !claimId) return

    clearTimeout(searchTimer)
    searchTimer = setTimeout(() => {
      if (isProcedure) {
        dispatch(versantProcedureCodesLoad(claimId, {
          search_string: value,
          date_of_service: dateOfService,
          service_category: serviceCategories,
          sourceSubSystem,
        }))
      } else {
        dispatch(versantDiagnosisCodesLoad(claimId, {
          search_string: value,
          date_of_service: dateOfService,
        }))
      }
    }, 500)
  }

  const handleToggle = (data) => {
    const alreadySelected = selectedCodes.some((item) => item.code === data.code)
    if (alreadySelected) {
      const nextCodes = selectedCodes.filter((item) => item.code !== data.code)
      input.onChange(nextCodes.length ? nextCodes : null)
      return
    }

    input.onChange([...selectedCodes, toCodePayload(data)])
  }

  const handleRemove = (code) => {
    const nextCodes = selectedCodes.filter((item) => item.code !== code)
    input.onChange(nextCodes.length ? nextCodes : null)
  }

  const resetValue = (event) => {
    event.stopPropagation()
    dispatch(change(meta.form, input.name, null))
    setSearchText('')
  }

  const openMenu = () => {
    setOpen(true)
    setTimeout(() => searchInputRef.current?.focus(), 0)
  }

  return (
    <div className={`${wrapperClasses} autocomplete`} ref={rootRef}>
      {label && <label>{label}</label>}
      <div className={`dropdown border rounded autocomplete-field form-control ${open ? 'show' : ''}`}>
        <button
          type="button"
          className="selected dropdown-toggle d-flex justify-content-between w-100 border-0 bg-transparent p-0 text-left"
          aria-haspopup="true"
          aria-expanded={open}
          onClick={openMenu}
        >
          <span className="mr-7 overflow-hidden autocomplete-tooltip">
            {selectedCodes.length ? `${selectedCodes.length} selected` : 'Select...'}
          </span>
          <span className="la la-angle-down text-right text-muted" />
        </button>
        {selectedCodes.length > 0 && (
          <span className="la la-times-circle text-right text-muted reset-close pointer" onClick={resetValue} />
        )}

        <div
          className={`dropdown-menu dropdown-search w-100 ${open ? 'show' : ''}`}
          style={{ maxHeight: '30rem', display: open ? 'block' : undefined }}
          onMouseDown={(event) => event.preventDefault()}
        >
          <div className="p-5 d-flex align-items-center">
            <input
              ref={searchInputRef}
              className="form-control w-100"
              placeholder="Type at least 3 letters..."
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              onMouseDown={(event) => event.stopPropagation()}
            />
            {loading && <div className="spinner-border spinner-border-sm text-primary ml-2" role="status" />}
          </div>
          {results.length > 0 ? (
            <div className="overflow-auto h-50">
              {results.map((data) => {
                const checked = selectedCodes.some((item) => item.code === data.code)
                return (
                  <button
                    type="button"
                    key={data.code}
                    className="dropdown-item d-flex align-items-center text-left"
                    onMouseDown={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      handleToggle(data)
                    }}
                  >
                    <input
                      type="checkbox"
                      className="mr-3"
                      checked={checked}
                      readOnly
                      tabIndex={-1}
                    />
                    <span className="py-2 text-wrap">{data.label}</span>
                  </button>
                )
              })}
            </div>
          ) : (
            searchText.length >= MIN_SEARCH_LENGTH && !loading && (
              <div className="overflow-auto h-50 text-center">
                <p className="mb-3">No record found</p>
              </div>
            )
          )}
        </div>
      </div>

      {selectedCodes.length > 0 && (
        <div className="mt-3">
          {selectedCodes.map((code) => (
            <span key={code.code} className="badge badge-light border mr-2 mb-2 p-2 font-weight-normal">
              {formatVersantCodeLabel(code)}
              <button type="button" className="btn btn-link btn-sm p-0 ml-2 align-baseline" onClick={() => handleRemove(code.code)}>
                &times;
              </button>
            </span>
          ))}
        </div>
      )}

      {touched && error && <span className="error-msg text-danger d-inline la-sm">{error}</span>}
    </div>
  )
}

export default connect((state, { input }) => ({
  codes: state.versantVisionInsurance[isProcedureField(input.name) ? 'procedure_codes' : 'diagnosis_codes'] || [],
  loading: state.versantVisionInsurance.saving,
}))(VersantSearchSelect)
